import { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import {
  Calendar, Calendar as CalendarIcon, Clock, Users, Plus, ExternalLink,
  ChevronLeft, ChevronRight, CheckCircle2, Video, Phone, Settings, MapPin,
  Copy, Check, ShieldCheck, RefreshCw, User, Sparkles
} from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger, DialogDescription
} from "@/components/ui/dialog";
import { toast } from "sonner";

const DEFAULT_APPOINTMENTS = [
  {
    id: "apt-101",
    client: "Elena Rostova",
    company: "Grand Haven Hotel",
    title: "Commercial Photoshoot Pre-Production",
    time: "10:00 AM - 10:45 AM",
    date: "Today, Nov 2",
    day: "Mon",
    hour: 10,
    channel: "Google Meet",
    status: "Confirmed",
  },
  {
    id: "apt-102",
    client: "Marcus Vance",
    company: "Skyline Real Estate",
    title: "Drone Lookbook & Video Ad Review",
    time: "02:00 PM - 02:30 PM",
    date: "Tomorrow, Nov 3",
    day: "Tue",
    hour: 14,
    channel: "Google Meet",
    status: "Confirmed",
  },
  {
    id: "apt-103",
    client: "Sophia Rossi",
    company: "Rossi Design Studio",
    title: "Executive Portrait Retouching Approval",
    time: "11:30 AM - 12:00 PM",
    date: "Wednesday, Nov 4",
    day: "Wed",
    hour: 11,
    channel: "Phone Call",
    status: "Confirmed",
  },
  {
    id: "apt-104",
    client: "David Miller",
    company: "Apex Media Group",
    title: "VIP Retainer Strategy Consultation",
    time: "04:00 PM - 04:30 PM",
    date: "Thursday, Nov 5",
    day: "Thu",
    hour: 16,
    channel: "In-Studio Session",
    status: "Confirmed",
  }
];

export default function Calendars() {
  const [appointments, setAppointments] = useState(() => {
    try {
      const saved = localStorage.getItem("ld_calendar_appointments");
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed) && parsed.length > 0) return parsed;
      }
    } catch {}
    return DEFAULT_APPOINTMENTS;
  });

  const [view, setView] = useState("week"); // 'day', 'week', 'month'
  const [newOpen, setNewOpen] = useState(false);
  const [syncOpen, setSyncOpen] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);

  // New Appointment Form
  const [form, setForm] = useState({
    client: "",
    title: "",
    time: "10:00 AM - 10:30 AM",
    date: "Today, Nov 2",
    channel: "Google Meet"
  });

  // Sync Settings State
  const [syncSettings, setSyncSettings] = useState(() => {
    try {
      const saved = localStorage.getItem("ld_calendar_sync_settings");
      if (saved) return JSON.parse(saved);
    } catch {}
    return {
      googleSync: true,
      googleEmail: "colibrisocialclub@gmail.com",
      outlookSync: false,
      startTime: "09:00 AM",
      endTime: "06:00 PM",
      bufferMinutes: "15",
      timezone: "America/Los_Angeles (Pacific Time)"
    };
  });

  const publicBookingUrl = "https://deck.weddingsthyzfoto.com/book/master";

  // Reload appointments when new booking happens from public portal
  useEffect(() => {
    const handleStorageUpdate = () => {
      try {
        const saved = localStorage.getItem("ld_calendar_appointments");
        if (saved) setAppointments(JSON.parse(saved));
      } catch {}
    };
    window.addEventListener("calendar_appointment_added", handleStorageUpdate);
    window.addEventListener("storage", handleStorageUpdate);
    return () => {
      window.removeEventListener("calendar_appointment_added", handleStorageUpdate);
      window.removeEventListener("storage", handleStorageUpdate);
    };
  }, []);

  const handleCreate = (e) => {
    e.preventDefault();
    if (!form.client || !form.title) {
      toast.error("Please enter client name and appointment title");
      return;
    }
    const newA = {
      id: `apt-${Date.now()}`,
      client: form.client,
      title: form.title,
      time: form.time,
      date: form.date,
      day: "Mon",
      channel: form.channel,
      status: "Confirmed",
    };
    const updated = [newA, ...appointments];
    setAppointments(updated);
    try {
      localStorage.setItem("ld_calendar_appointments", JSON.stringify(updated));
    } catch {}
    setNewOpen(false);
    toast.success("Appointment scheduled & calendar invitation dispatched!");
    setForm({ client: "", title: "", time: "10:00 AM - 10:30 AM", date: "Today, Nov 2", channel: "Google Meet" });
  };

  const handleSaveSyncSettings = (e) => {
    e.preventDefault();
    try {
      localStorage.setItem("ld_calendar_sync_settings", JSON.stringify(syncSettings));
    } catch {}
    toast.success("Calendar sync settings & working hours updated!");
    setSyncOpen(false);
  };

  const handleCopyBookingLink = () => {
    navigator.clipboard.writeText(publicBookingUrl);
    setCopiedLink(true);
    toast.success("Public booking link copied to clipboard!");
    setTimeout(() => setCopiedLink(false), 2000);
  };

  const hoursList = [
    "08:00 AM", "09:00 AM", "10:00 AM", "11:00 AM", "12:00 PM",
    "01:00 PM", "02:00 PM", "03:00 PM", "04:00 PM", "05:00 PM"
  ];

  const weekDays = [
    { label: "Mon", full: "Nov 2" },
    { label: "Tue", full: "Nov 3" },
    { label: "Wed", full: "Nov 4" },
    { label: "Thu", full: "Nov 5" },
    { label: "Fri", full: "Nov 6" },
    { label: "Sat", full: "Nov 7" },
    { label: "Sun", full: "Nov 8" },
  ];

  return (
    <div className="p-6 sm:p-8 lg:p-10 space-y-6 max-w-[1600px] mx-auto w-full" data-testid="calendars-page">
      {/* Top Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b pb-6" style={{ borderColor: "var(--border)" }}>
        <div>
          <h2 className="text-3xl font-black tracking-tight font-heading" style={{ color: "var(--text)" }}>
            Calendars &amp; Appointments
          </h2>
          <p className="text-sm mt-1" style={{ color: "var(--text-soft)" }}>
            Schedule client discovery calls, sync with Google &amp; Outlook Calendar, and automate SMS reminders.
          </p>
        </div>

        <div className="flex items-center gap-2">
          {/* View Toggle */}
          <div className="flex items-center rounded-xl border p-1 bg-slate-100 dark:bg-slate-900" style={{ borderColor: "var(--border)" }}>
            {["day", "week", "month"].map(v => (
              <button
                key={v}
                onClick={() => setView(v)}
                className={`px-3.5 py-1.5 rounded-lg text-xs font-bold capitalize transition-all cursor-pointer ${
                  view === v
                    ? "bg-white dark:bg-slate-800 shadow-xs text-blue-600 dark:text-blue-400"
                    : "text-slate-500 hover:text-slate-800 dark:hover:text-slate-200"
                }`}
              >
                {v}
              </button>
            ))}
          </div>

          <Button
            size="sm"
            onClick={() => setNewOpen(true)}
            className="bg-blue-600 hover:bg-blue-700 text-white font-bold text-sm gap-1.5 h-9 px-4 shadow-xs"
          >
            <Plus className="w-4 h-4" />
            <span>New Appointment</span>
          </Button>
        </div>
      </div>

      {/* Booking Link Banner */}
      <Card
        className="p-4 border flex flex-col sm:flex-row sm:items-center justify-between gap-4 rounded-xl"
        style={{ background: "var(--surface)", borderColor: "var(--border)" }}
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-blue-600 text-white flex items-center justify-center shrink-0 shadow-xs">
            <Clock className="w-5 h-5" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="font-bold text-sm" style={{ color: "var(--text)" }}>Public Booking Link Active</span>
              <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
            </div>
            <div className="text-xs text-slate-500 font-mono mt-0.5">{publicBookingUrl}</div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleCopyBookingLink}
            className="text-xs font-semibold gap-1.5 h-8"
          >
            {copiedLink ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}
            <span>{copiedLink ? "Copied!" : "Copy Link"}</span>
          </Button>

          <Button
            variant="outline"
            size="sm"
            onClick={() => window.open(publicBookingUrl, "_blank")}
            className="text-xs font-semibold gap-1.5 h-8"
          >
            <ExternalLink className="w-3.5 h-3.5 text-blue-600" />
            <span>Open Booking Page</span>
          </Button>

          <Button
            variant="outline"
            size="sm"
            onClick={() => setSyncOpen(true)}
            className="text-xs font-semibold gap-1.5 h-8"
          >
            <Settings className="w-3.5 h-3.5 text-slate-500" />
            <span>Sync Settings</span>
          </Button>
        </div>
      </Card>

      {/* ─────────────────────────────────────────────────────────────
          CALENDAR VIEWS (DAY, WEEK, MONTH)
      ───────────────────────────────────────────────────────────── */}

      {/* VIEW 1: WEEK VIEW (Visual 7-Day Grid) */}
      {view === "week" && (
        <Card className="border overflow-hidden rounded-xl" style={{ background: "var(--surface)", borderColor: "var(--border)" }}>
          <div className="p-4 border-b flex items-center justify-between" style={{ borderColor: "var(--border)" }}>
            <div className="flex items-center gap-3">
              <h3 className="font-bold text-base" style={{ color: "var(--text)" }}>Week of November 2 – 8, 2026</h3>
              <Badge variant="outline" className="text-xs bg-emerald-50 text-emerald-700 border-emerald-300">
                {appointments.length} Confirmed Sessions
              </Badge>
            </div>
            <div className="flex items-center gap-1.5 text-xs text-slate-500">
              <span className="w-2.5 h-2.5 rounded-full bg-blue-500" /> Google Meet
              <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 ml-2" /> Phone Call
              <span className="w-2.5 h-2.5 rounded-full bg-purple-500 ml-2" /> In-Studio
            </div>
          </div>

          <div className="grid grid-cols-7 border-b text-center text-xs font-bold divide-x" style={{ borderColor: "var(--border)", background: "color-mix(in srgb, var(--surface) 96%, black)" }}>
            {weekDays.map(d => (
              <div key={d.label} className="py-3 px-1">
                <span className="text-slate-400 uppercase text-[10px]">{d.label}</span>
                <div className="text-sm font-extrabold text-slate-800 dark:text-slate-100">{d.full}</div>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-7 divide-x min-h-[360px]" style={{ borderColor: "var(--border)" }}>
            {weekDays.map((d, colIdx) => {
              const dayApts = appointments.filter((_, idx) => (idx % 7) === colIdx);
              return (
                <div key={d.label} className="p-2 space-y-2.5 hover:bg-slate-50/50 dark:hover:bg-slate-900/30 transition-colors">
                  {dayApts.map(apt => (
                    <div
                      key={apt.id}
                      className="p-2.5 rounded-xl border bg-white dark:bg-slate-900 shadow-2xs space-y-1 text-xs border-l-4 border-l-blue-600 hover:shadow-xs transition-shadow"
                      style={{ borderColor: "var(--border)" }}
                    >
                      <div className="font-bold text-slate-900 dark:text-slate-100 truncate">{apt.client}</div>
                      <div className="text-[11px] text-slate-500 line-clamp-1">{apt.title}</div>
                      <div className="text-[10px] font-mono text-blue-600 font-semibold">{apt.time}</div>
                    </div>
                  ))}
                  {dayApts.length === 0 && (
                    <div className="text-center py-8 text-[11px] text-slate-400">Available</div>
                  )}
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {/* VIEW 2: DAY VIEW (Hourly Timeline) */}
      {view === "day" && (
        <Card className="border overflow-hidden rounded-xl" style={{ background: "var(--surface)", borderColor: "var(--border)" }}>
          <div className="p-4 border-b flex items-center justify-between" style={{ borderColor: "var(--border)" }}>
            <h3 className="font-bold text-base" style={{ color: "var(--text)" }}>Today's Schedule — Monday, Nov 2, 2026</h3>
            <span className="text-xs text-slate-500 font-semibold">Pacific Standard Time (PST)</span>
          </div>

          <div className="divide-y" style={{ borderColor: "var(--border)" }}>
            {hoursList.map(h => {
              const match = appointments.find(a => a.time.includes(h.slice(0, 2)));
              return (
                <div key={h} className="p-3.5 flex items-start gap-4 hover:bg-slate-50/50 dark:hover:bg-slate-900/30 transition-colors">
                  <span className="w-20 font-mono text-xs font-semibold text-slate-400 shrink-0">{h}</span>
                  {match ? (
                    <div className="flex-1 p-3 rounded-xl bg-blue-50/70 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-900 flex items-center justify-between">
                      <div>
                        <div className="font-bold text-sm text-blue-950 dark:text-blue-100">{match.title}</div>
                        <div className="text-xs text-blue-700 dark:text-blue-300 mt-0.5">
                          Client: <strong>{match.client}</strong> · {match.channel}
                        </div>
                      </div>
                      <Badge className="bg-blue-600 text-white text-xs">{match.time}</Badge>
                    </div>
                  ) : (
                    <div className="flex-1 text-xs text-slate-400 italic py-1">Open time slot</div>
                  )}
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {/* VIEW 3: MONTH VIEW (30-Day Grid) */}
      {view === "month" && (
        <Card className="border overflow-hidden rounded-xl" style={{ background: "var(--surface)", borderColor: "var(--border)" }}>
          <div className="p-4 border-b flex items-center justify-between" style={{ borderColor: "var(--border)" }}>
            <h3 className="font-bold text-base" style={{ color: "var(--text)" }}>November 2026</h3>
            <span className="text-xs text-slate-500 font-semibold">30 Days · Master Studio Calendar</span>
          </div>

          <div className="grid grid-cols-7 border-b text-center text-xs font-bold divide-x" style={{ borderColor: "var(--border)", background: "color-mix(in srgb, var(--surface) 96%, black)" }}>
            {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map(d => (
              <div key={d} className="py-2.5 text-slate-500 uppercase text-[10px]">{d}</div>
            ))}
          </div>

          <div className="grid grid-cols-7 divide-x divide-y" style={{ borderColor: "var(--border)" }}>
            {[...Array(30)].map((_, i) => {
              const dayNum = i + 1;
              const hasApt = dayNum <= 5;
              return (
                <div key={i} className="min-h-[75px] p-2 hover:bg-slate-50/50 dark:hover:bg-slate-900/30 transition-colors">
                  <div className="font-bold text-xs text-slate-600 dark:text-slate-300">{dayNum}</div>
                  {hasApt && (
                    <div className="mt-1 px-1.5 py-0.5 rounded bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 text-[10px] font-semibold truncate">
                      {appointments[dayNum % appointments.length]?.client}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {/* ─────────────────────────────────────────────────────────────
          UPCOMING APPOINTMENTS LIST (LEDGER)
      ───────────────────────────────────────────────────────────── */}
      <Card className="border overflow-hidden rounded-xl" style={{ background: "var(--surface)", borderColor: "var(--border)" }}>
        <div className="p-4 border-b flex items-center justify-between" style={{ borderColor: "var(--border)" }}>
          <h3 className="font-bold text-base" style={{ color: "var(--text)" }}>Confirmed Booking Records</h3>
          <span className="text-xs text-slate-500 font-semibold">{appointments.length} Total Bookings</span>
        </div>
        <div className="divide-y" style={{ borderColor: "var(--border)" }}>
          {appointments.map(apt => (
            <div key={apt.id} className="p-4 hover:bg-slate-50 dark:hover:bg-slate-800/40 transition-colors flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-xl bg-emerald-50 dark:bg-emerald-950/40 text-emerald-600 flex items-center justify-center shrink-0">
                  {apt.channel.includes("Meet") || apt.channel.includes("Zoom") ? (
                    <Video className="w-5 h-5" />
                  ) : (
                    <Phone className="w-5 h-5" />
                  )}
                </div>
                <div>
                  <div className="font-bold text-sm" style={{ color: "var(--text)" }}>{apt.title}</div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    Client: <span className="font-semibold text-slate-800 dark:text-slate-200">{apt.client}</span> · {apt.channel}
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-4 self-end sm:self-center">
                <div className="text-right">
                  <div className="text-xs font-bold" style={{ color: "var(--text)" }}>{apt.date}</div>
                  <div className="text-xs text-slate-500 font-mono">{apt.time}</div>
                </div>
                <Badge variant="outline" className="text-xs bg-emerald-50 text-emerald-700 border-emerald-300">
                  {apt.status}
                </Badge>
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* ── New Appointment Modal ── */}
      <Dialog open={newOpen} onOpenChange={setNewOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Book New Appointment</DialogTitle>
            <DialogDescription>Schedule a meeting directly on your master calendar.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4 py-2">
            <div>
              <Label className="text-xs font-semibold text-slate-500 uppercase">Client Name</Label>
              <Input
                value={form.client}
                onChange={(e) => setForm({ ...form, client: e.target.value })}
                placeholder="e.g. Michael Scott"
                className="mt-1 text-sm"
                required
              />
            </div>
            <div>
              <Label className="text-xs font-semibold text-slate-500 uppercase">Meeting Title / Topic</Label>
              <Input
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="e.g. Commercial Shoot Discovery"
                className="mt-1 text-sm"
                required
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-semibold text-slate-500 uppercase">Date</Label>
                <Input
                  value={form.date}
                  onChange={(e) => setForm({ ...form, date: e.target.value })}
                  placeholder="Today, Nov 2"
                  className="mt-1 text-sm font-mono"
                />
              </div>
              <div>
                <Label className="text-xs font-semibold text-slate-500 uppercase">Time Slot</Label>
                <Input
                  value={form.time}
                  onChange={(e) => setForm({ ...form, time: e.target.value })}
                  placeholder="10:00 AM - 10:30 AM"
                  className="mt-1 text-sm font-mono"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs font-semibold text-slate-500 uppercase">Location / Meeting Channel</Label>
              <select
                value={form.channel}
                onChange={(e) => setForm({ ...form, channel: e.target.value })}
                className="w-full mt-1 h-10 rounded-md border px-3 text-sm bg-transparent"
                style={{ borderColor: "var(--border)", color: "var(--text)" }}
              >
                <option value="Google Meet">Google Meet (Auto-generated link)</option>
                <option value="Phone Call">Phone Call (+1 SMS reminder)</option>
                <option value="Zoom Meeting">Zoom Video Conference</option>
                <option value="In-Studio Session">In-Studio On-Site Visit</option>
              </select>
            </div>
            <DialogFooter className="pt-2">
              <Button type="button" variant="ghost" onClick={() => setNewOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-blue-600 hover:bg-blue-700 text-white font-bold text-sm">
                Confirm Booking
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* ── Sync Settings Modal ── */}
      <Dialog open={syncOpen} onOpenChange={setSyncOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Calendar Sync &amp; Availability Settings</DialogTitle>
            <DialogDescription>
              Configure 2-way sync with Google &amp; Outlook, working hours, and appointment buffers.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSaveSyncSettings} className="space-y-4 py-2">
            <div className="p-3.5 rounded-xl border bg-slate-50 dark:bg-slate-900 space-y-2">
              <div className="flex items-center justify-between">
                <span className="font-bold text-xs" style={{ color: "var(--text)" }}>Google Calendar 2-Way Sync</span>
                <input
                  type="checkbox"
                  checked={syncSettings.googleSync}
                  onChange={(e) => setSyncSettings({ ...syncSettings, googleSync: e.target.checked })}
                  className="rounded w-4 h-4"
                />
              </div>
              <Input
                value={syncSettings.googleEmail}
                onChange={(e) => setSyncSettings({ ...syncSettings, googleEmail: e.target.value })}
                placeholder="colibrisocialclub@gmail.com"
                className="text-xs font-mono"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-semibold text-slate-500 uppercase">Day Start</Label>
                <Input
                  value={syncSettings.startTime}
                  onChange={(e) => setSyncSettings({ ...syncSettings, startTime: e.target.value })}
                  className="mt-1 text-xs font-mono"
                />
              </div>
              <div>
                <Label className="text-xs font-semibold text-slate-500 uppercase">Day End</Label>
                <Input
                  value={syncSettings.endTime}
                  onChange={(e) => setSyncSettings({ ...syncSettings, endTime: e.target.value })}
                  className="mt-1 text-xs font-mono"
                />
              </div>
            </div>

            <div>
              <Label className="text-xs font-semibold text-slate-500 uppercase">Buffer Between Appointments</Label>
              <select
                value={syncSettings.bufferMinutes}
                onChange={(e) => setSyncSettings({ ...syncSettings, bufferMinutes: e.target.value })}
                className="w-full mt-1 h-9 rounded-md border px-3 text-sm bg-transparent"
                style={{ borderColor: "var(--border)", color: "var(--text)" }}
              >
                <option value="0">0 minutes</option>
                <option value="10">10 minutes</option>
                <option value="15">15 minutes (Recommended)</option>
                <option value="30">30 minutes</option>
              </select>
            </div>

            <DialogFooter className="pt-2">
              <Button type="button" variant="ghost" onClick={() => setSyncOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-blue-600 hover:bg-blue-700 text-white font-bold text-sm">
                Save Sync Settings
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
